import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Paper,
  Stack,
  Avatar,
  Badge,
  Tooltip,
} from '@mui/material';
import {
  EventNote as EventNoteIcon,
  Add as AddIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CreditCard as CreditCardIcon,
  Store as StoreIcon,
  LocalOffer as OfferIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  SmartToy as SmartToyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  AccountBalanceWallet as WalletIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { planningService, ScheduledPurchase, CreateScheduledPurchase } from '../services/planning';
import { cardService } from '../services/cards';

const PURCHASE_TYPES = [
  { value: 'GROCERIES', label: 'Groceries', icon: '🛒', color: '#10b981' },
  { value: 'DINING', label: 'Dining', icon: '🍽️', color: '#f59e0b' },
  { value: 'SHOPPING', label: 'Shopping', icon: '🛍️', color: '#6366f1' },
  { value: 'FUEL', label: 'Fuel', icon: '⛽', color: '#ef4444' },
  { value: 'TRAVEL', label: 'Travel', icon: '✈️', color: '#3b82f6' },
  { value: 'ENTERTAINMENT', label: 'Entertainment', icon: '🎬', color: '#ec4899' },
  { value: 'UTILITIES', label: 'Utilities', icon: '💡', color: '#8b5cf6' },
  { value: 'ONLINE_SHOPPING', label: 'Online Shopping', icon: '🛒', color: '#06b6d4' },
  { value: 'OTHER', label: 'Other', icon: '📦', color: '#64748b' },
];

// Helper function to format reward rate preserving exact decimals (e.g., 0.75 stays 0.75, not 0.8)
// This matches SmartRecommendations display format exactly
const formatRewardRate = (rate: number | string | undefined): string => {
  if (rate === undefined || rate === null) return '0';
  
  // Convert to number
  const numValue = typeof rate === 'string' ? parseFloat(rate) : rate;
  if (isNaN(numValue)) return '0';
  
  // Format to exactly 2 decimal places, then remove trailing zeros
  // This ensures 0.75 displays as "0.75" (not "0.8" or "0.750")
  let formatted = numValue.toFixed(2);
  
  // Remove trailing zeros, but preserve the decimal point if there are non-zero decimals
  // e.g., "0.75" stays "0.75", "1.50" becomes "1.5", "2.00" becomes "2"
  formatted = formatted.replace(/\.?0+$/, '');
  
  return formatted || '0';
};

const Planning: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [statusFilter, setStatusFilter] = useState<'SCHEDULED' | 'COMPLETED' | 'CANCELLED'>('SCHEDULED');
  const [expandedMerchants, setExpandedMerchants] = useState<{ [key: number]: boolean }>({});
  const queryClient = useQueryClient();

  const { data: purchases = [], isLoading } = useQuery<ScheduledPurchase[]>({
    queryKey: ['scheduledPurchases', statusFilter],
    queryFn: () => planningService.getScheduledPurchases(statusFilter),
  });

  const { data: userCards = [] } = useQuery({
    queryKey: ['userCards'],
    queryFn: cardService.getUserCards,
  });

  const createMutation = useMutation({
    mutationFn: planningService.createScheduledPurchase,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledPurchases'] });
      setOpenDialog(false);
      setSnackbar({ open: true, message: 'Purchase scheduled successfully!', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to schedule purchase', severity: 'error' });
    },
  });

  const completeMutation = useMutation({
    mutationFn: planningService.completeScheduledPurchase,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledPurchases'] });
      setSnackbar({ open: true, message: 'Purchase marked as completed!', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to complete purchase', severity: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: planningService.deleteScheduledPurchase,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledPurchases'] });
      setSnackbar({ open: true, message: 'Purchase cancelled', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to cancel purchase', severity: 'error' });
    },
  });

  const regenerateMutation = useMutation({
    mutationFn: planningService.regenerateRecommendations,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduledPurchases'] });
      setSnackbar({ open: true, message: 'Recommendations regenerated successfully!', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: 'Failed to regenerate recommendations', severity: 'error' });
    },
  });

  const handleSubmit = (data: CreateScheduledPurchase) => {
    createMutation.mutate(data);
  };

  const handleComplete = (id: number) => {
    if (window.confirm('Mark this purchase as completed?')) {
      completeMutation.mutate(id);
    }
  };

  const handleCancel = (id: number) => {
    if (window.confirm('Cancel this scheduled purchase?')) {
      deleteMutation.mutate(id);
    }
  };

  const toggleMerchantExpansion = (purchaseId: number) => {
    setExpandedMerchants(prev => ({ ...prev, [purchaseId]: !prev[purchaseId] }));
  };

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', pb: 6 }}>
      <Container maxWidth="xl">
        {/* Modern Header Section */}
        <Box sx={{ 
          mb: 4, 
          pt: 4,
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
          borderRadius: 4,
          p: 4,
          border: '1px solid rgba(99, 102, 241, 0.2)',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 3 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(99, 102, 241, 0.4)',
              }}
            >
              <EventNoteIcon sx={{ color: 'white', fontSize: 40 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h3" component="h1" fontWeight={800} gutterBottom sx={{ 
                background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                Smart Planning
              </Typography>
              <Typography variant="h6" color="text.secondary" fontWeight={500}>
                Schedule purchases and maximize savings with AI-powered recommendations
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => setOpenDialog(true)}
              sx={{
                background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                px: 4,
                py: 1.5,
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #db2777 100%)',
                  boxShadow: '0 6px 16px rgba(99, 102, 241, 0.5)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Schedule Purchase
            </Button>
          </Box>

          {/* Status Filters */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip
              label="Scheduled"
              onClick={() => setStatusFilter('SCHEDULED')}
              sx={{
                bgcolor: statusFilter === 'SCHEDULED' ? 'primary.main' : 'white',
                color: statusFilter === 'SCHEDULED' ? 'white' : 'text.primary',
                fontWeight: 600,
                px: 1,
                py: 2.5,
                fontSize: '0.9375rem',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: statusFilter === 'SCHEDULED' ? 'primary.dark' : '#f1f5f9',
                },
                transition: 'all 0.2s ease',
              }}
            />
            <Chip
              label="Completed"
              onClick={() => setStatusFilter('COMPLETED')}
              sx={{
                bgcolor: statusFilter === 'COMPLETED' ? '#10b981' : 'white',
                color: statusFilter === 'COMPLETED' ? 'white' : 'text.primary',
                fontWeight: 600,
                px: 1,
                py: 2.5,
                fontSize: '0.9375rem',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: statusFilter === 'COMPLETED' ? '#059669' : '#f1f5f9',
                },
                transition: 'all 0.2s ease',
              }}
            />
            <Chip
              label="Cancelled"
              onClick={() => setStatusFilter('CANCELLED')}
              sx={{
                bgcolor: statusFilter === 'CANCELLED' ? '#ef4444' : 'white',
                color: statusFilter === 'CANCELLED' ? 'white' : 'text.primary',
                fontWeight: 600,
                px: 1,
                py: 2.5,
                fontSize: '0.9375rem',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: statusFilter === 'CANCELLED' ? '#dc2626' : '#f1f5f9',
                },
                transition: 'all 0.2s ease',
              }}
            />
          </Box>
        </Box>

        {/* My Wallet Section - Modern Design */}
        <Card sx={{ 
          mb: 4, 
          borderRadius: 4, 
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid #e5e7eb',
          overflow: 'hidden',
        }}>
          <Box sx={{
            background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
            p: 3,
            color: 'white',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
                  <WalletIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    My Wallet
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {userCards.length} card{userCards.length !== 1 ? 's' : ''} available for recommendations
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                size="medium"
                startIcon={<CreditCardIcon />}
                onClick={() => window.location.href = '/cards'}
                sx={{ 
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Manage Cards
              </Button>
            </Box>
          </Box>
          <CardContent sx={{ p: 3 }}>
            {userCards.length === 0 ? (
              <Alert 
                severity="warning" 
                sx={{ 
                  borderRadius: 3,
                  bgcolor: '#fef3c7',
                  border: '1px solid #fbbf24',
                }}
              >
                <Typography variant="body1" fontWeight={600} gutterBottom>
                  No cards in your wallet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Add at least one Trinidad & Tobago credit card to get personalized recommendations.
                </Typography>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<AddIcon />}
                  onClick={() => window.location.href = '/cards'}
                  sx={{ 
                    background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                    textTransform: 'none',
                  }}
                >
                  Add Your First Card
                </Button>
              </Alert>
            ) : (
              <Grid container spacing={2.5}>
                {userCards.map((userCard: any) => {
                  const isRecommended = purchases.some(p => p.recommended_card_detail?.id === userCard.card?.id);
                  const purchaseType = PURCHASE_TYPES.find(t => 
                    purchases.some(p => p.recommended_card_detail?.id === userCard.card?.id && p.purchase_type === t.value)
                  );
                  return (
                    <Grid item xs={12} sm={6} md={4} key={userCard.id}>
                      <Paper
                        elevation={0}
                        sx={{
                          p: 3,
                          border: isRecommended ? '2px solid #6366f1' : '2px solid #e5e7eb',
                          borderRadius: 3,
                          bgcolor: isRecommended 
                            ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(236, 72, 153, 0.08) 100%)'
                            : 'white',
                          background: isRecommended 
                            ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(236, 72, 153, 0.08) 100%)'
                            : 'white',
                          transition: 'all 0.3s ease',
                          position: 'relative',
                          overflow: 'hidden',
                          '&::before': isRecommended ? {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: 'linear-gradient(90deg, #6366f1 0%, #ec4899 100%)',
                          } : {},
                          '&:hover': {
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            transform: 'translateY(-4px)',
                            borderColor: isRecommended ? '#6366f1' : '#cbd5e1',
                          },
                        }}
                      >
                        {isRecommended && (
                          <Chip
                            icon={<StarIcon sx={{ fontSize: 16 }} />}
                            label="Recommended"
                            size="small"
                            sx={{ 
                              position: 'absolute',
                              top: 12,
                              right: 12,
                              bgcolor: '#6366f1',
                              color: 'white',
                              fontWeight: 700,
                              fontSize: '0.75rem',
                              boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)',
                            }}
                          />
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'start', gap: 2, mb: 2 }}>
                          <Avatar
                            sx={{
                              width: 56,
                              height: 56,
                              bgcolor: 'primary.main',
                              fontSize: '1.5rem',
                              fontWeight: 700,
                            }}
                          >
                            <CreditCardIcon />
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" fontWeight={700} gutterBottom sx={{ 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {userCard.card?.name || 'Unknown Card'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                              {userCard.card?.bank?.name || ''}
                            </Typography>
                            {userCard.card_number_last4 && (
                              <Typography variant="caption" color="text.secondary" sx={{ 
                                fontFamily: 'monospace',
                                letterSpacing: 2,
                              }}>
                                •••• {userCard.card_number_last4}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                          {/* Show cashback only if card has cashback_rate and no reward_points_rate, or if explicitly cashback type */}
                          {userCard.card?.cashback_rate && 
                           userCard.card?.cashback_rate > 0 && 
                           (!userCard.card?.reward_points_rate || userCard.card?.reward_points_rate === 0) && (
                            <Chip
                              icon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
                              label={`${Number(userCard.card.cashback_rate).toFixed(1)}% Cashback`}
                              size="small"
                              sx={{ 
                                bgcolor: '#10b981',
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '0.8125rem',
                              }}
                            />
                          )}
                          {/* Show points/miles only if card has reward_points_rate and no cashback_rate */}
                          {userCard.card?.reward_points_rate && 
                           userCard.card?.reward_points_rate > 0 && 
                           (!userCard.card?.cashback_rate || userCard.card?.cashback_rate === 0) && (
                            <Chip
                              icon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
                              label={`${Number(userCard.card.reward_points_rate).toFixed(1)}x Points`}
                              size="small"
                              sx={{ 
                                bgcolor: '#6366f1',
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '0.8125rem',
                              }}
                            />
                          )}
                          {/* Show "No Rewards" if neither cashback nor points */}
                          {(!userCard.card?.cashback_rate || userCard.card?.cashback_rate === 0) && 
                           (!userCard.card?.reward_points_rate || userCard.card?.reward_points_rate === 0) && (
                            <Chip
                              label="No Rewards"
                              size="small"
                              sx={{ 
                                bgcolor: '#94a3b8',
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '0.8125rem',
                              }}
                            />
                          )}
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </CardContent>
        </Card>

        {/* Smart Recommendations Banner */}
        {purchases.length > 0 && statusFilter === 'SCHEDULED' && (
          <Card sx={{ 
            mb: 4, 
            borderRadius: 4, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
            border: '1px solid rgba(99, 102, 241, 0.3)',
          }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}>
                  <SmartToyIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700}>
                    Smart Recommendations Active
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    AI-powered analysis of your purchases, offers, and spending patterns
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Purchases Grid */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress size={48} />
          </Box>
        ) : purchases.length === 0 ? (
          <Card sx={{ 
            borderRadius: 4, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #e5e7eb',
          }}>
            <CardContent sx={{ textAlign: 'center', py: 8 }}>
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <EventNoteIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5 }} />
              </Box>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                No {statusFilter.toLowerCase()} purchases
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
                {statusFilter === 'SCHEDULED'
                  ? 'Schedule your upcoming purchases to get personalized recommendations and maximize your savings'
                  : `You haven't ${statusFilter === 'COMPLETED' ? 'completed' : 'cancelled'} any purchases yet`}
              </Typography>
              {statusFilter === 'SCHEDULED' && (
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenDialog(true)}
                  sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
                    '&:hover': {
                      boxShadow: '0 6px 16px rgba(99, 102, 241, 0.5)',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  Schedule Your First Purchase
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {purchases.map((purchase) => {
              const purchaseTypeInfo = PURCHASE_TYPES.find(t => t.value === purchase.purchase_type);
              const recommendedCardId = purchase.recommended_card_detail?.id;
              const isInWallet = userCards.some((uc: any) => uc.card?.id === recommendedCardId);
              const backendValidation = purchase.recommended_card_detail?._is_in_wallet !== false;
              const showWarning = purchase.recommended_card_detail && (!isInWallet || !backendValidation);

              return (
                <Grid item xs={12} lg={6} key={purchase.id}>
                  <Card 
                    sx={{ 
                      borderRadius: 4, 
                      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      border: '1px solid #e5e7eb',
                      transition: 'all 0.3s ease',
                      overflow: 'hidden',
                      position: 'relative',
                      '&:hover': {
                        boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    {/* Card Header with Gradient */}
                    <Box sx={{
                      background: purchaseTypeInfo 
                        ? `linear-gradient(135deg, ${purchaseTypeInfo.color}15 0%, ${purchaseTypeInfo.color}08 100%)`
                        : 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
                      p: 3,
                      borderBottom: '1px solid #f3f4f6',
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                            <Chip
                              icon={<span style={{ fontSize: '1.2rem' }}>{purchaseTypeInfo?.icon || '📦'}</span>}
                              label={purchaseTypeInfo?.label || purchase.purchase_type}
                              size="small"
                              sx={{ 
                                bgcolor: purchaseTypeInfo?.color || 'primary.main',
                                color: 'white',
                                fontWeight: 700,
                                fontSize: '0.875rem',
                                height: 32,
                              }}
                            />
                            {purchase.status === 'SCHEDULED' && (
                              <Chip
                                label="Scheduled"
                                size="small"
                                sx={{ 
                                  bgcolor: '#f0fdf4',
                                  color: '#16a34a',
                                  fontWeight: 600,
                                  border: '1px solid #86efac',
                                }}
                              />
                            )}
                          </Box>
                          <Typography variant="h5" fontWeight={700} gutterBottom>
                            {purchase.merchant_preference || 'Scheduled Purchase'}
                          </Typography>
                          {purchase.estimated_amount && (
                            <Typography variant="h4" fontWeight={800} sx={{ 
                              background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
                              WebkitBackgroundClip: 'text',
                              WebkitTextFillColor: 'transparent',
                              backgroundClip: 'text',
                              mb: 2,
                            }}>
                              TT${Number(purchase.estimated_amount).toFixed(2)}
                            </Typography>
                          )}
                        </Box>
                        {purchase.status === 'SCHEDULED' && (
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="Regenerate recommendations">
                              <IconButton
                                size="small"
                                onClick={() => regenerateMutation.mutate(purchase.id)}
                                disabled={regenerateMutation.isPending}
                                sx={{ 
                                  bgcolor: 'rgba(99, 102, 241, 0.1)',
                                  '&:hover': { bgcolor: 'rgba(99, 102, 241, 0.2)' }
                                }}
                              >
                                <RefreshIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Mark as completed">
                              <IconButton
                                size="small"
                                onClick={() => handleComplete(purchase.id)}
                                sx={{ 
                                  bgcolor: 'rgba(16, 185, 129, 0.1)',
                                  '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.2)' }
                                }}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Cancel purchase">
                              <IconButton
                                size="small"
                                onClick={() => handleCancel(purchase.id)}
                                sx={{ 
                                  bgcolor: 'rgba(239, 68, 68, 0.1)',
                                  '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.2)' }
                                }}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <CalendarIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                          <Typography variant="body1" fontWeight={500}>
                            {new Date(purchase.scheduled_date).toLocaleString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true,
                            })}
                          </Typography>
                        </Box>
                        {purchase.location && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <LocationIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                            <Typography variant="body1" fontWeight={500}>
                              {purchase.location}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Box>

                    <CardContent sx={{ flex: 1, p: 3 }}>
                      {/* Recommended Card Section - Modern Design */}
                      {purchase.recommended_card_detail && (
                        <Box 
                          sx={{ 
                            mb: 3,
                            p: 3,
                            background: showWarning 
                              ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(239, 68, 68, 0.05) 100%)'
                              : 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
                            borderRadius: 3,
                            border: showWarning 
                              ? '2px solid #ef4444'
                              : '2px solid rgba(99, 102, 241, 0.3)',
                            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.15)',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              height: 4,
                              background: showWarning 
                                ? 'linear-gradient(90deg, #ef4444 0%, #dc2626 100%)'
                                : 'linear-gradient(90deg, #6366f1 0%, #ec4899 100%)',
                            },
                          }}
                        >
                          {showWarning && (
                            <Alert 
                              severity="warning" 
                              sx={{ 
                                mb: 2,
                                borderRadius: 2,
                                bgcolor: '#fef3c7',
                                border: '1px solid #fbbf24',
                              }}
                            >
                              <Typography variant="body2" fontWeight={600}>
                                The recommended card may not be in your wallet. Click refresh to regenerate.
                              </Typography>
                            </Alert>
                          )}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
                            <Avatar sx={{ 
                              bgcolor: 'primary.main', 
                              width: 56, 
                              height: 56,
                              boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
                            }}>
                              <CreditCardIcon sx={{ fontSize: 28 }} />
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="h6" fontWeight={700} gutterBottom>
                                Recommended Card {isInWallet ? '(From Your Wallet)' : ''}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Best card for this purchase
                              </Typography>
                            </Box>
                          </Box>
                          
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2.5,
                              bgcolor: 'white',
                              borderRadius: 2,
                              border: '1px solid #e5e7eb',
                            }}
                          >
                            <Typography variant="h6" fontWeight={700} gutterBottom>
                              {purchase.recommended_card_detail.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 1 }}>
                              {purchase.recommended_card_detail.bank.name}
                            </Typography>
                            {purchase.recommended_card_detail.bank.operating_category && (
                              <Chip
                                label={
                                  purchase.recommended_card_detail.bank.operating_category === 'TRADITIONAL'
                                    ? 'Traditional Bank'
                                    : purchase.recommended_card_detail.bank.operating_category === 'NON_TRADITIONAL'
                                    ? 'Non-traditional Bank'
                                    : 'Unknown Category'
                                }
                                size="small"
                                sx={{
                                  bgcolor: purchase.recommended_card_detail.bank.is_traditional_bank
                                    ? 'rgba(99, 102, 241, 0.1)'
                                    : 'rgba(245, 158, 11, 0.1)',
                                  color: purchase.recommended_card_detail.bank.is_traditional_bank
                                    ? '#6366f1'
                                    : '#f59e0b',
                                  fontWeight: 600,
                                  border: '1px solid',
                                  borderColor: purchase.recommended_card_detail.bank.is_traditional_bank
                                    ? '#6366f1'
                                    : '#f59e0b',
                                  mb: 2,
                                }}
                              />
                            )}
                           
                          </Paper>
                          
                          
                        </Box>
                      )}

                      {!purchase.recommended_card_detail && (
                        <Alert 
                          severity="info" 
                          sx={{ 
                            mb: 3,
                            borderRadius: 3,
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            No recommendation yet. Click the refresh button to generate recommendations.
                          </Typography>
                        </Alert>
                      )}

                      
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* Schedule Purchase Dialog */}
        <SchedulePurchaseDialog
          open={openDialog}
          onClose={() => setOpenDialog(false)}
          onSubmit={handleSubmit}
          isLoading={createMutation.isPending}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            severity={snackbar.severity} 
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            sx={{ borderRadius: 2 }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

interface SchedulePurchaseDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateScheduledPurchase) => void;
  isLoading: boolean;
}

const SchedulePurchaseDialog: React.FC<SchedulePurchaseDialogProps> = ({ open, onClose, onSubmit, isLoading }) => {
  // Helper function to format date for datetime-local input (YYYY-MM-DDTHH:mm)
  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      // Get local date components
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch (e) {
      return '';
    }
  };

  // Helper function to convert datetime-local value to ISO string for backend
  const convertToISOString = (datetimeLocal: string): string => {
    if (!datetimeLocal) return '';
    try {
      // datetime-local format: YYYY-MM-DDTHH:mm
      // Convert to Date object (treats as local time)
      const date = new Date(datetimeLocal);
      if (isNaN(date.getTime())) return '';
      
      // Return ISO string for backend
      return date.toISOString();
    } catch (e) {
      return '';
    }
  };

  const [formData, setFormData] = useState<CreateScheduledPurchase>({
    purchase_type: 'GROCERIES',
    scheduled_date: '',
    estimated_amount: undefined,
    location: '',
    merchant_preference: '',
    notes: '',
  });

  // Local state for datetime-local input (formatted value)
  const [localDateTime, setLocalDateTime] = useState<string>('');

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setFormData({
        purchase_type: 'GROCERIES',
        scheduled_date: '',
        estimated_amount: undefined,
        location: '',
        merchant_preference: '',
        notes: '',
      });
      setLocalDateTime('');
    }
  }, [open]);

  const handleChange = (field: keyof CreateScheduledPurchase) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (field === 'scheduled_date') {
      // Handle datetime-local input separately
      const datetimeValue = e.target.value;
      setLocalDateTime(datetimeValue);
      // Convert to ISO string for backend
      const isoString = convertToISOString(datetimeValue);
      setFormData({ ...formData, scheduled_date: isoString });
    } else {
      const value = field === 'estimated_amount' ? parseFloat(e.target.value) || undefined : e.target.value;
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleSubmit = () => {
    if (!formData.scheduled_date || !localDateTime) {
      alert('Please select a date and time');
      return;
    }
    onSubmit(formData);
    setFormData({
      purchase_type: 'GROCERIES',
      scheduled_date: '',
      estimated_amount: undefined,
      location: '',
      merchant_preference: '',
      notes: '',
    });
    setLocalDateTime('');
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
        },
      }}
    >
      <Box sx={{
        background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
        p: 3,
        color: 'white',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={700}>
            Schedule Purchase
          </Typography>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={2.5}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              select
              label="Purchase Type"
              value={formData.purchase_type}
              onChange={handleChange('purchase_type')}
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              {PURCHASE_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{type.icon}</span>
                    <span>{type.label}</span>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Date & Time"
              type="datetime-local"
              value={localDateTime}
              onChange={handleChange('scheduled_date')}
              InputLabelProps={{ shrink: true }}
              required
              inputProps={{
                min: new Date().toISOString().slice(0, 16), // Prevent selecting past dates
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              helperText="Select the date and time for your purchase"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Estimated Amount (TT$)"
              type="number"
              value={formData.estimated_amount || ''}
              onChange={handleChange('estimated_amount')}
              inputProps={{ min: 0, step: 0.01 }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Location"
              value={formData.location}
              onChange={handleChange('location')}
              placeholder="e.g., Port of Spain"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Merchant Preference (Optional)"
              value={formData.merchant_preference}
              onChange={handleChange('merchant_preference')}
              placeholder="e.g., Massy Stores, KFC"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes (Optional)"
              value={formData.notes}
              onChange={handleChange('notes')}
              multiline
              rows={3}
              placeholder="Any additional notes..."
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 3, borderTop: '1px solid #f3f4f6' }}>
        <Button 
          onClick={onClose} 
          disabled={isLoading}
          sx={{ 
            textTransform: 'none',
            fontWeight: 600,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || !formData.scheduled_date}
          sx={{
            background: 'linear-gradient(135deg, #6366f1 0%, #ec4899 100%)',
            px: 4,
            py: 1.25,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5 0%, #db2777 100%)',
              boxShadow: '0 6px 16px rgba(99, 102, 241, 0.5)',
            },
          }}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
        >
          Schedule Purchase
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default Planning;